/**
 * ViewSupportTickets - Support ticket list page
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Paperclip } from "lucide-react";
import { Pagination } from "@/ui/atoms";
import { cn } from "@/utils/utils";
import type {
    TApiSupportTicketResponse,
    TApiSupportTicketStatus,
} from "@/apis/proda_finance/types";

const STATUS_COLORS: Record<string, string> = {
    open: "bg-blue-100 text-blue-700",
    in_review: "bg-blue-100 text-blue-700",
    needs_info: "bg-blue-100 text-blue-700",
    rejected: "bg-red-100 text-red-700",
    forwarded: "bg-blue-100 text-blue-700",
    closed: "bg-green-100 text-green-700",
};

const STATUS_LABELS: Record<string, string> = {
    open: "Open",
    in_review: "In Review",
    needs_info: "Needs Info",
    rejected: "Rejected",
    forwarded: "Forwarded",
    closed: "Closed",
};

const TYPE_COLORS: Record<string, string> = {
    bug: "bg-red-100 text-red-700",
    feature: "bg-purple-100 text-purple-700",
};

const TYPE_LABELS: Record<string, string> = {
    bug: "Bug",
    feature: "Feature",
};

const PRIORITY_COLORS: Record<string, string> = {
    low: "bg-gray-100 text-gray-700",
    normal: "bg-blue-100 text-blue-700",
    high: "bg-red-100 text-red-700",
};

const PRIORITY_LABELS: Record<string, string> = {
    low: "Low",
    normal: "Normal",
    high: "High",
};

const ALL_STATUSES: TApiSupportTicketStatus[] = [
    "open",
    "in_review",
    "needs_info",
    "rejected",
    "forwarded",
    "closed",
];

export type TViewSupportTicketsParams = {
    useCases: () => {
        loadTickets: (params: { page: number; pageSize: number; status?: string }) => Promise<{
            items: TApiSupportTicketResponse[];
            total: number;
        }>;
    };
};

export type TViewSupportTicketsProps = Record<string, never>;

export const createViewSupportTickets = ({ useCases }: TViewSupportTicketsParams) => {
    return function ViewSupportTickets() {
        const { loadTickets } = useCases();
        const navigate = useNavigate();
        const [searchParams, setSearchParams] = useSearchParams();

        const [tickets, setTickets] = useState<TApiSupportTicketResponse[]>([]);
        const [total, setTotal] = useState(0);
        const [isLoading, setIsLoading] = useState(true);
        const [error, setError] = useState<string | null>(null);

        const page = parseInt(searchParams.get("page") || "1", 10);
        const pageSize = 20;
        const statusFilter = searchParams.get("status") || undefined;

        const fetchTickets = useCallback(async () => {
            setIsLoading(true);
            setError(null);
            try {
                const result = await loadTickets({
                    page,
                    pageSize,
                    status: statusFilter,
                });
                setTickets(result.items);
                setTotal(result.total);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to load tickets");
            } finally {
                setIsLoading(false);
            }
        }, [loadTickets, page, pageSize, statusFilter]);

        useEffect(() => {
            fetchTickets();
        }, [fetchTickets]);

        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-semibold">Support Tickets</h1>
                </div>

                {/* Status filter */}
                <div className="flex items-center gap-1 rounded-lg border bg-muted/40 p-1 w-fit">
                    {[
                        { value: undefined, label: "All" },
                        ...ALL_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] })),
                    ].map((item) => (
                        <button
                            key={item.value ?? "all"}
                            onClick={() => {
                                const params = new URLSearchParams(searchParams);
                                if (item.value) {
                                    params.set("status", item.value);
                                } else {
                                    params.delete("status");
                                }
                                params.set("page", "1");
                                setSearchParams(params);
                            }}
                            className={cn(
                                "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                                statusFilter === item.value || (!statusFilter && !item.value)
                                    ? "bg-background text-foreground shadow-sm"
                                    : "text-muted-foreground hover:text-foreground",
                            )}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>

                {error && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                        {error}
                    </div>
                )}

                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                    </div>
                ) : tickets.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">No tickets found.</div>
                ) : (
                    <>
                        <div className="rounded-md border">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b bg-muted/50">
                                        <th className="px-4 py-3 text-left font-medium">Title</th>
                                        <th className="px-4 py-3 text-left font-medium w-24">
                                            Type
                                        </th>
                                        <th className="px-4 py-3 text-left font-medium w-24">
                                            Priority
                                        </th>
                                        <th className="px-4 py-3 text-left font-medium w-28">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-left font-medium w-40">
                                            Created By
                                        </th>
                                        <th className="px-4 py-3 text-left font-medium w-36">
                                            Creation Date
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tickets.map((ticket) => (
                                        <tr
                                            key={ticket.id}
                                            className="border-b cursor-pointer hover:bg-muted/30 transition-colors even:bg-muted/40"
                                            onClick={() => navigate(`/support/${ticket.id}`)}
                                        >
                                            <td className="px-4 py-3">
                                                <span className="font-medium">
                                                    {ticket.title}
                                                    {ticket.has_screenshot && (
                                                        <Paperclip className="inline-block h-3.5 w-3.5 ml-1.5 text-muted-foreground" />
                                                    )}
                                                </span>
                                                {ticket.comment_count > 0 && (
                                                    <span className="ml-2 text-xs text-muted-foreground">
                                                        ({ticket.comment_count}{" "}
                                                        {ticket.comment_count === 1
                                                            ? "comment"
                                                            : "comments"}
                                                        )
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span
                                                    className={cn(
                                                        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                                                        TYPE_COLORS[ticket.ticket_type] ||
                                                            "bg-gray-100 text-gray-700",
                                                    )}
                                                >
                                                    {TYPE_LABELS[ticket.ticket_type] ||
                                                        ticket.ticket_type}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span
                                                    className={cn(
                                                        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                                                        PRIORITY_COLORS[ticket.priority] ||
                                                            "bg-gray-100 text-gray-700",
                                                    )}
                                                >
                                                    {PRIORITY_LABELS[ticket.priority] ||
                                                        ticket.priority}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span
                                                    className={cn(
                                                        "inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium",
                                                        STATUS_COLORS[ticket.status] ||
                                                            "bg-gray-100 text-gray-800",
                                                    )}
                                                >
                                                    {STATUS_LABELS[ticket.status] || ticket.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground">
                                                {ticket.created_by_display_name}
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground">
                                                {new Date(ticket.created_at).toLocaleDateString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            total={total}
                            onPageChange={(newPage) => {
                                const params = new URLSearchParams(searchParams);
                                params.set("page", String(newPage));
                                setSearchParams(params);
                            }}
                        />
                    </>
                )}
            </div>
        );
    };
};
